import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./DrawerAnimation.css";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import menuSvg from "../../assets/svg/navigationIcons/menu.svg";
import editProfileActiveSvg from "../../assets/svg/navigationIcons/edit-profile-active.svg";
import editProfileInactiveSvg from "../../assets/svg/navigationIcons/edit-profile-inactive.svg";
import homeActiveSvg from "../../assets/svg/navigationIcons/home-active.svg";
import homeInactiveSvg from "../../assets/svg/navigationIcons/home-inactive.svg";
import createMealActiveSvg from "../../assets/svg/navigationIcons/create-meal-active.svg";
import createMealInactiveSvg from "../../assets/svg/navigationIcons/create-meal-inactive.svg";
import orderHistoryActiveSvg from "../../assets/svg/navigationIcons/order-history-active.svg";
import orderHistoryInactiveSvg from "../../assets/svg/navigationIcons/order-history-inactive.svg";
import gamesActiveSvg from "../../assets/svg/navigationIcons/game-challenges-active.svg";
import gamesInactiveSvg from "../../assets/svg/navigationIcons/game-challenges-inactive.svg";
import adminActiveSvg from "../../assets/svg/navigationIcons/admin-active.svg";
import adminInactiveSvg from "../../assets/svg/navigationIcons/admin-inactive.svg";
import signOutSvg from "../../assets/svg/navigationIcons/sign-out.svg";
import createMealInactiveGreySvg from "../../assets/svg/create-meal-inactive-grey.svg";
import informationSvg from "../../assets/svg/information.svg";
import { DrawerMenuButton } from "./DrawerMenuButton";
import { Logout } from "../../store/actions/authActions/Logout";
import { MealResponse } from "types";
import { RefreshMoneySavedData } from "store/actions/mealsActions/RefreshMoneySavedData";
import { Popover } from "react-tiny-popover";
import { useGlobalState } from "GlobalStateContext";
import { IsRunningOverwolf } from "utilityFunctions";
import { HashLink } from "react-router-hash-link";

type Props = {
  orderMealIsLoadingState?: boolean;
  setRefreshMeal?: React.Dispatch<React.SetStateAction<boolean>>;
  userMeals: MealResponse[];
};

export const DrawerMenu: React.FC<Props> = ({
  orderMealIsLoadingState,
  setRefreshMeal,
  userMeals,
}) => {
  const { setAccountPageIndex } = useGlobalState();
  const userInfo = useAppSelector((state: any) => state.auth.userInfo);
  const usingTestingAccount = userInfo?.roles?.includes("TESTER");
  const { t } = useTranslation();
  const language = t("localization_testing");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const refreshToken = useAppSelector((state) => state.auth.refreshToken);
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const { hasSeenGameChallenges } = useGlobalState();
  const drawerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const isRunningOverwolf = IsRunningOverwolf();
  const { pathname } = useLocation();
  const onMealsPage = pathname.slice(0, 18) === "/dashboard/mymeals";
  const onCheckOutPage = pathname.slice(0, 19) === "/dashboard/checkout";
  const orderMealIsLoading = orderMealIsLoadingState && onCheckOutPage;

  // Persist showDrawerMenu state across route changes
  const [showDrawerMenu, setShowDrawerMenu] = useState<boolean>(() => {
    const storedState = localStorage.getItem("showDrawerMenu");
    return storedState ? JSON.parse(storedState) : true;
  });

  useEffect(() => {
    localStorage.setItem("showDrawerMenu", JSON.stringify(showDrawerMenu));
  }, [showDrawerMenu]);

  const moneySavedAmount = useAppSelector((state) => state.other.moneySaved);

  const REACT_APP_GAME_CHALLENGES_ENABLED = useAppSelector(
    (state) => state?.auth?.env?.REACT_APP_GAME_CHALLENGES_ENABLED
  );
  const REACT_APP_GAME_CHALLENGES_PRIVATE_MODE = useAppSelector(
    (state) => state?.auth?.env?.REACT_APP_GAME_CHALLENGES_PRIVATE_MODE
  );

  useEffect(() => {
    if (showDrawerMenu) {
      dispatch(
        RefreshMoneySavedData(accessToken, refreshToken, userInfo, navigate)
      );
    }
  }, [showDrawerMenu, accessToken, refreshToken, userInfo, navigate, dispatch]);

  const paymentCardToUse =
    userInfo?.address?.country === "UNITED_STATES"
      ? userInfo?.paymentCards.USD
      : userInfo?.paymentCards.CAD;
  const additionalInfoRequired =
    !userInfo?.phone ||
    !paymentCardToUse ||
    !userInfo?.emailVerified ||
    userInfo.lastName === "Unspecified" ||
    userInfo.firstName === "Unspecified";

  const logoutHandler = () => {
    if (setRefreshMeal) {
      setRefreshMeal(true);
    }
    window.localStorage.removeItem("user");
    window.sessionStorage.clear();
    dispatch(Logout(accessToken, refreshToken, userInfo, navigate));
  };

  useEffect(() => {
    localStorage.setItem("showDrawerMenu", JSON.stringify(showDrawerMenu));
  }, [showDrawerMenu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        drawerRef.current &&
        overlayRef.current &&
        !drawerRef.current.contains(event.target as Node) &&
        !overlayRef.current.contains(event.target as Node)
      ) {
        setShowDrawerMenu(false);
      }
    };
    if (showDrawerMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDrawerMenu]);

  useEffect(() => {
    setShowDrawerMenu(false);
  }, [location]);

  return (
    <div className="relative">
      {/* Overlay */}
      {showDrawerMenu && (
        <div
          ref={overlayRef}
          className="fixed inset-0 bg-black bg-opacity-40 z-20"
          onClick={() => setShowDrawerMenu(false)}
        />
      )}

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`${
          !accessToken ? "hidden" : "hidden md:block"
        } fixed h-full bg-slate-800 text-slate-200 md:border-r-2 border-[#395279] shadow-2xl bottom-0 z-30 ${
          showDrawerMenu ? "w-72 overflow-hidden" : "w-16 3xl:w-20"
        } transition-all duration-500 ease-in-out`}
      >
        {/* Header */}
        {showDrawerMenu ? (
          <div
            className={` ${
              isRunningOverwolf ? "pt-20" : "pt-10"
            } w-full flex justify-between`}
          >
            <div className="flex text-xl ml-8 pr-1 font-bold">
              <span>{t("meals.dashboard")}</span>
            </div>
            <button
              onClick={() => setShowDrawerMenu(false)}
              className="hover:opacity-70 transition ease-in-out duration-200"
            >
              <img className="h-5 3xl:h-6 pr-8" src={menuSvg} alt="menu" />
            </button>
          </div>
        ) : (
          <div
            className={`${
              isRunningOverwolf ? "pt-20" : "pt-10"
            } w-full flex justify-center pt-10`}
          >
            <button
              onClick={(e) => {
                if (orderMealIsLoading) {
                  e.preventDefault();
                  return;
                }
                setShowDrawerMenu(true);
              }}
              className="hover:opacity-70 transition ease-in-out duration-200"
            >
              <img className="h-5 3xl:h-6" src={menuSvg} alt="menu" />
            </button>
          </div>
        )}

        {/* Menu Items */}
        <ul
          className={`w-full text-xl space-y-8 3xl:space-y-10 flex flex-col my-10 ${
            showDrawerMenu
              ? "pl-4"
              : isRunningOverwolf
              ? "ml-1.5 3xl:mx-3"
              : "ml-1 3xl:mx-3"
          }`}
        >
          {/* My Account */}
          <li>
            <HashLink
              to={orderMealIsLoading ? "#" : "/account/info"}
              draggable={false}
              onClick={(e) => {
                if (orderMealIsLoading) {
                  e.preventDefault();
                  return;
                }
                setAccountPageIndex(1);
              }}
            >
              <div className="flex items-center">
                <div className="relative group">
                  <DrawerMenuButton
                    showDrawerMenu={showDrawerMenu}
                    active={location.pathname === "/account/info"}
                    icon={
                      location.pathname === "/account/info"
                        ? editProfileActiveSvg
                        : editProfileInactiveSvg
                    }
                    label={t("meals.myAccount")}
                  />
                </div>
                {/* Additional badge */}
                <div className="my-auto z-50">
                  {additionalInfoRequired && !usingTestingAccount && (
                    <div
                      className={`h-4 w-4 select-none text-sm flex justify-center text-white items-center rounded-full bg-red-500 ${
                        showDrawerMenu ? "-ml-2" : "-ml-6 mt-3"
                      }`}
                    >
                      !
                    </div>
                  )}
                </div>
              </div>
            </HashLink>
          </li>
          {/* My Meals */}
          <li>
            <Link
              to={orderMealIsLoading ? "#" : "/dashboard/mymeals"}
              draggable={false}
            >
              <div className="flex items-center">
                <div className="relative group">
                  <DrawerMenuButton
                    showDrawerMenu={showDrawerMenu}
                    active={location.pathname === "/dashboard/mymeals"}
                    icon={onMealsPage ? homeActiveSvg : homeInactiveSvg}
                    label={t("meals.myMeals")}
                  />
                </div>
              </div>
            </Link>
          </li>
          {/* Create Meal */}
          <li>
            {userMeals?.length !== 10 ? (
              <Link
                to={orderMealIsLoading ? "#" : "/dashboard/restlist"}
                draggable={false}
              >
                <div className="flex items-center">
                  <div className="relative group">
                    <DrawerMenuButton
                      showDrawerMenu={showDrawerMenu}
                      active={location.pathname === "/dashboard/restlist"}
                      icon={
                        location.pathname === "/dashboard/restlist"
                          ? createMealActiveSvg
                          : createMealInactiveSvg
                      }
                      label={t("meals.createMeal")}
                    />
                  </div>
                </div>
              </Link>
            ) : (
              <div className="text-base text-gray-400">
                <div className="flex items-center">
                  <div className="relative group">
                    <DrawerMenuButton
                      showDrawerMenu={showDrawerMenu}
                      icon={createMealInactiveGreySvg}
                      label={t("mealCreator.mealLimit")}
                    />
                    {/* Tooltip */}
                    <div className="absolute w-36 left-12 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 bg-gray-700 text-white text-sm rounded py-1 px-2 z-50">
                      {t("mealCreator.mealLimit")}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </li>
          {/* My Orders */}
          <li>
            <Link
              to={orderMealIsLoading ? "#" : "/dashboard/orderhistory"}
              draggable={false}
            >
              <div className="flex items-center">
                <div className="relative group">
                  <DrawerMenuButton
                    showDrawerMenu={showDrawerMenu}
                    active={location.pathname === "/dashboard/orderhistory"}
                    icon={
                      location.pathname === "/dashboard/orderhistory"
                        ? orderHistoryActiveSvg
                        : orderHistoryInactiveSvg
                    }
                    label={t("meals.myOrders")}
                  />
                </div>
              </div>
            </Link>
          </li>

          {/* Game Challenges */}
          {REACT_APP_GAME_CHALLENGES_ENABLED === "true" &&
            (REACT_APP_GAME_CHALLENGES_PRIVATE_MODE === "false" ||
              userInfo?.accessToPrivateChallenges) && (
              <li>
                <Link
                  to={orderMealIsLoading ? "#" : "/gamechallenges/progress"}
                  draggable={false}
                >
                  <div className="flex items-center">
                    <div className="relative group">
                      <DrawerMenuButton
                        showDrawerMenu={showDrawerMenu}
                        active={location.pathname.includes("/gamechallenges")}
                        icon={
                          location.pathname.includes("/gamechallenges")
                            ? gamesActiveSvg
                            : gamesInactiveSvg
                        }
                        label={t("gameChallenges.gameChallenges")}
                      />
                    </div>
                    <div className="my-auto z-50">
                      {hasSeenGameChallenges === "notSeen" && (
                        <div
                          className={`h-4 w-4 select-none text-sm flex justify-center text-white items-center rounded-full bg-red-500 ${
                            showDrawerMenu ? "-ml-2" : "-ml-6 mt-3"
                          }`}
                        >
                          !
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            )}
          <ul className="absolute bottom-8">
            {/* Admin */}
            {userInfo?.roles?.includes("ADMIN") && (
              <li>
                <Link
                  to={orderMealIsLoading ? "#" : "/admin/users"}
                  draggable={false}
                >
                  <div className="flex items-center">
                    <div className="relative group">
                      <DrawerMenuButton
                        showDrawerMenu={showDrawerMenu}
                        active={location.pathname === "/admin/users"}
                        icon={
                          location.pathname === "/admin/users"
                            ? adminActiveSvg
                            : adminInactiveSvg
                        }
                        label={t("admin.admin")}
                      />
                    </div>
                  </div>
                </Link>
              </li>
            )}
            <li>
              <div className="flex items-center">
                <div className="relative group">
                  <DrawerMenuButton
                    showDrawerMenu={showDrawerMenu}
                    onClick={() => {
                      if (!orderMealIsLoading) {
                        logoutHandler();
                      }
                    }}
                    icon={signOutSvg}
                    label={t("form.logout")}
                  />
                </div>
              </div>
            </li>
          </ul>
        </ul>

        {/* Money Saved Section */}
        {showDrawerMenu && (
          <div className="w-full text-lg ml-8">
            <p className="text-[#3B82F6]">{t("moneySaved.moneySaved")}</p>
            <div className="flex space-x-3">
              {language === "fr" ? (
                <p>{moneySavedAmount} $</p>
              ) : (
                <p>${moneySavedAmount}</p>
              )}
              <Popover
                containerStyle={{ zIndex: "100" }}
                onClickOutside={() => setShowPopover(false)}
                content={
                  <div className="bg-slate-900 w-64 p-1 rounded-md text-slate-200 border text-sm border-slate-200 ml-5">
                    <p>{t("moneySaved.moneySavedPopover")}</p>
                  </div>
                }
                isOpen={showPopover}
              >
                <button
                  className="hover:opacity-70 transition ease-in-out duration-200"
                  onClick={() => setShowPopover(!showPopover)}
                >
                  <img className="h-4 w-4" src={informationSvg} alt="info" />
                </button>
              </Popover>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DrawerMenu;
